export type ManuscriptAnalysisCategory =
  | 'adverbs'
  | 'fillerWords'
  | 'weakVerbs'
  | 'tellIndicators'
  | 'cliches'
  | 'pacingWeakeners'
  | 'redundancies';

export type ManuscriptAnalysisConfig = Record<ManuscriptAnalysisCategory, boolean>;
export type ManuscriptAnalysisCounts = Record<ManuscriptAnalysisCategory, number>;
export type ManuscriptAnalysisMatch = {
  category: ManuscriptAnalysisCategory;
  start: number;
  end: number;
};

export const MANUSCRIPT_ANALYSIS_CATEGORIES: ManuscriptAnalysisCategory[] = [
  'adverbs',
  'fillerWords',
  'weakVerbs',
  'tellIndicators',
  'cliches',
  'pacingWeakeners',
  'redundancies',
];

export const MANUSCRIPT_ANALYSIS_LABELS: Record<ManuscriptAnalysisCategory, string> = {
  adverbs: 'Adverbs',
  fillerWords: 'Filler Words',
  weakVerbs: 'Weak Verbs',
  tellIndicators: 'Tell Indicators',
  cliches: 'Cliches',
  pacingWeakeners: 'Pacing Weakeners',
  redundancies: 'Redundancies',
};

export const DEFAULT_MANUSCRIPT_ANALYSIS_CONFIG: ManuscriptAnalysisConfig = {
  adverbs: true,
  fillerWords: true,
  weakVerbs: true,
  tellIndicators: false,
  cliches: false,
  pacingWeakeners: false,
  redundancies: false,
};

export const EMPTY_MANUSCRIPT_ANALYSIS_COUNTS: ManuscriptAnalysisCounts = {
  adverbs: 0,
  fillerWords: 0,
  weakVerbs: 0,
  tellIndicators: 0,
  cliches: 0,
  pacingWeakeners: 0,
  redundancies: 0,
};

const FILLER_WORDS = new Set([
  'really', 'very', 'just', 'quite', 'somewhat', 'perhaps', 'maybe', 'actually', 'basically', 'literally',
  'simply', 'kind', 'kinda', 'sort', 'sorta', 'wirklich', 'sehr', 'einfach', 'eigentlich', 'irgendwie',
  'vielleicht', 'quasi', 'halt', 'bloß', 'nur', 'vraiment', 'très', 'juste', 'plutôt', 'peut-être',
  'realmente', 'muy', 'solo', 'quizás', 'talvez', 'semplicemente', 'davvero', 'molto', 'forse',
]);

const WEAK_VERBS = new Set([
  'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'get',
  'gets', 'got', 'make', 'makes', 'made', 'ist', 'sind', 'war', 'waren', 'sein', 'gewesen', 'wird',
  'werden', 'wurde', 'wurden', 'hat', 'haben', 'hatte', 'hatten', 'macht', 'machen', 'machte', 'gemacht',
  'tut', 'tun', 'tat', 'getan', 'bekommt', 'bekommen', 'bekam', 'kriegt', 'kriegen', 'kann', 'können',
  'konnte', 'muss', 'müssen', 'musste', 'soll', 'sollen', 'sollte', 'will', 'wollen', 'wollte',
]);

const TELL_INDICATORS = new Set([
  'felt', 'feel', 'feels', 'realized', 'realise', 'realises', 'realizes', 'knew', 'know', 'knows',
  'seemed', 'seem', 'seems', 'noticed', 'notice', 'understood', 'thought', 'thinks', 'think', 'fühlte',
  'fühlten', 'fühlt', 'fühlen', 'dachte', 'dachten', 'denken', 'wusste', 'wussten', 'wissen', 'merkte',
  'merkten', 'merken', 'bemerkte', 'bemerkten', 'bemerken', 'erkannte', 'erkannten', 'schien', 'schienen',
  'scheint',
]);

const GERMAN_ADVERBS = new Set([
  'langsam', 'schnell', 'plötzlich', 'sofort', 'häufig', 'selten', 'oft', 'kaum', 'bereits', 'jetzt',
  'heute', 'morgen', 'gestern', 'hier', 'dort', 'wirklich', 'vielleicht', 'natürlich', 'nicht', 'wieder',
  'weiter', 'dann', 'bald', 'später', 'früher', 'oben', 'unten', 'links', 'rechts', 'drinnen', 'draußen',
  'deshalb', 'darum', 'daher', 'dennoch', 'trotzdem', 'allerdings',
]);

const ADVERB_EXCEPTIONS = new Set(['family', 'only', 'early', 'supply', 'reply', 'apply', 'july']);

const PHRASES: Partial<Record<ManuscriptAnalysisCategory, string[]>> = {
  cliches: [
    'heart skipped a beat', 'chill ran down', 'blood ran cold', 'deafening silence', 'in the nick of time',
    'knuckles turned white', 'tears streamed down', 'a wave of relief', 'broke the silence', 'sigh of relief',
    'cold sweat', 'breath caught', 'stomach dropped', 'like an eternity', 'herz schlug schneller',
    'kalter schauer', 'eisige stille', 'totenstille', 'brach das schweigen', 'seufzer der erleichterung',
    'kalter schweiß', 'stockte der atem', 'magen drehte sich', 'kloß im hals', 'wie eine ewigkeit',
  ],
  pacingWeakeners: [
    'started to', 'began to', 'proceeded to', 'attempted to', 'tried to', 'continued to', 'managed to',
    'decided to', 'there was', 'there were', 'seemed to', 'appeared to', 'could feel', 'could see',
    'could hear', 'begann zu', 'fing an zu', 'versuchte zu', 'es gab', 'es war', 'schien zu',
    'konnte fühlen', 'konnte sehen', 'konnte hören',
  ],
  redundancies: [
    'nodded his head', 'nodded her head', 'shrugged his shoulders', 'shrugged her shoulders',
    'blinked his eyes', 'blinked her eyes', 'clenched his fists', 'clenched her fists',
    'completely destroyed', 'completely finished', 'totally unique', 'very unique', 'free gift',
    'past history', 'end result', 'close proximity', 'added bonus', 'brief moment', 'exact same',
    'nickte mit dem kopf', 'zuckte mit den schultern', 'blinzelte mit den augen', 'ballte die fäuste',
    'völlig zerstört', 'komplett fertig', 'kurzer moment', 'grundlegende basis',
  ],
};

const classifyWord = (
  rawWord: string,
  config: ManuscriptAnalysisConfig
): ManuscriptAnalysisCategory | null => {
  const word = rawWord.replace(/^'+|'+$/g, '').toLowerCase();
  if (!word) return null;
  if (config.tellIndicators && TELL_INDICATORS.has(word)) return 'tellIndicators';
  if (config.fillerWords && FILLER_WORDS.has(word)) return 'fillerWords';
  if (config.weakVerbs && WEAK_VERBS.has(word)) return 'weakVerbs';
  if (
    config.adverbs &&
    ((word.length >= 4 && word.endsWith('ly') && !ADVERB_EXCEPTIONS.has(word)) ||
      word.endsWith('weise') ||
      word.endsWith('erweise') ||
      word.endsWith('mente') ||
      GERMAN_ADVERBS.has(word))
  ) {
    return 'adverbs';
  }
  return null;
};

export const findManuscriptAnalysisMatches = (
  text: string,
  config: ManuscriptAnalysisConfig
): ManuscriptAnalysisMatch[] => {
  const matches: ManuscriptAnalysisMatch[] = [];
  for (const wordMatch of text.matchAll(/[\p{L}']+/gu)) {
    if (typeof wordMatch.index !== 'number') continue;
    const category = classifyWord(wordMatch[0], config);
    if (category) {
      matches.push({ category, start: wordMatch.index, end: wordMatch.index + wordMatch[0].length });
    }
  }

  const lower = text.toLowerCase();
  for (const category of MANUSCRIPT_ANALYSIS_CATEGORIES) {
    if (!config[category]) continue;
    for (const phrase of PHRASES[category] ?? []) {
      let offset = 0;
      while (offset < lower.length) {
        const start = lower.indexOf(phrase, offset);
        if (start < 0) break;
        matches.push({ category, start, end: start + phrase.length });
        offset = start + phrase.length;
      }
    }
  }

  return matches.sort((a, b) => a.start - b.start || b.end - a.end);
};

export const countManuscriptAnalysisMatches = (
  text: string,
  config: ManuscriptAnalysisConfig
): ManuscriptAnalysisCounts => {
  const counts = { ...EMPTY_MANUSCRIPT_ANALYSIS_COUNTS };
  findManuscriptAnalysisMatches(text, config).forEach(({ category }) => {
    counts[category] += 1;
  });
  return counts;
};
